import LockOpenIcon from '@mui/icons-material/LockOpen';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HistoryIcon from '@mui/icons-material/History';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';

import useRoles from 'common/hooks/useRoles';

const ManualAdjustmentButton = ({
  data,
  onAdjustClick,
  onReorderConfigClick,
  onStartCountClick,
  onManageCountClick,
  onCountHistoryClick,
  onCancelCountClick,
  onEditDefaultClick,
  showPhysicalCount,
}) => {
  const hasAccess = useRoles();
  const [anchorEl, setAnchorEl] = useState(null);

  if (!hasAccess.manualInventoryAdjust && !hasAccess.performPhysicalCount) return null;

  const isLockForCount = data?.isLockForCount ?? false;
  const allLocked = data?.allLocked ?? false;
  // The row's lot count only counts lots with stock, but a depleted lot can still be adjusted
  // in, so it no longer gates this menu. The lot selector loads lots itself and reports when
  // there genuinely are none.
  const adjustDisabled = isLockForCount || allLocked;
  const showCount = showPhysicalCount && hasAccess.performPhysicalCount;
  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleAdjust = () => {
    handleClose();
    onAdjustClick?.(data);
  };

  const handleReorderConfig = () => {
    handleClose();
    onReorderConfigClick?.(data);
  };

  const handleStartCount = () => {
    handleClose();
    onStartCountClick?.(data);
  };

  const handleManageCount = () => {
    handleClose();
    onManageCountClick?.(data);
  };

  const handleCountHistory = () => {
    handleClose();
    onCountHistoryClick?.(data);
  };

  const handleCancelCount = () => {
    handleClose();
    onCancelCountClick?.(data);
  };

  const handleEditDefault = () => {
    handleClose();
    onEditDefaultClick?.(data);
  };

  const adjustItem = (
    <MenuItem onClick={handleAdjust} disabled={adjustDisabled}>
      <ListItemIcon>
        <EditNoteIcon fontSize='small' />
      </ListItemIcon>
      <ListItemText>Manual Adjustment</ListItemText>
    </MenuItem>
  );

  let adjustMenuItem = null;
  if (hasAccess.manualInventoryAdjust) {
    if (allLocked) {
      adjustMenuItem = (
        <Tooltip title='All facilities are locked for physical count' placement='left'>
          <span>{adjustItem}</span>
        </Tooltip>
      );
    } else if (isLockForCount) {
      adjustMenuItem = (
        <Tooltip title='Item is locked for physical count' placement='left'>
          <span>{adjustItem}</span>
        </Tooltip>
      );
    } else {
      adjustMenuItem = adjustItem;
    }
  }

  return (
    <>
      <IconButton size='small' onClick={handleOpen} aria-label='inventory actions'>
        <TuneIcon fontSize='small' />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {adjustMenuItem}
        {hasAccess.manualInventoryAdjust && onReorderConfigClick && (
          <MenuItem onClick={handleReorderConfig}>
            <ListItemIcon>
              <TuneIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText>Edit Reorder Config</ListItemText>
          </MenuItem>
        )}
        {hasAccess.manualInventoryAdjust && onEditDefaultClick && (
          <MenuItem onClick={handleEditDefault}>
            <ListItemIcon>
              <StarOutlineIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText>Edit Default NDC</ListItemText>
          </MenuItem>
        )}
        {showCount && <Divider />}
        {showCount && !isLockForCount && (
          <MenuItem onClick={handleStartCount}>
            <ListItemIcon>
              <FactCheckIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText>Start Physical Count</ListItemText>
          </MenuItem>
        )}
        {showCount && isLockForCount && (
          <MenuItem onClick={handleManageCount}>
            <ListItemIcon>
              <FactCheckIcon fontSize='small' color='warning' />
            </ListItemIcon>
            <ListItemText>Manage Physical Count</ListItemText>
          </MenuItem>
        )}
        {showCount && isLockForCount && (
          <MenuItem onClick={handleCancelCount}>
            <ListItemIcon>
              <LockOpenIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText>Cancel Lock</ListItemText>
          </MenuItem>
        )}
        {showCount && (
          <MenuItem onClick={handleCountHistory}>
            <ListItemIcon>
              <HistoryIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText>Physical Count History</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

ManualAdjustmentButton.propTypes = {
  data: PropTypes.shape({ isLockForCount: PropTypes.bool, allLocked: PropTypes.bool }),
  onAdjustClick: PropTypes.func,
  onReorderConfigClick: PropTypes.func,
  onStartCountClick: PropTypes.func,
  onManageCountClick: PropTypes.func,
  onCountHistoryClick: PropTypes.func,
  onCancelCountClick: PropTypes.func,
  onEditDefaultClick: PropTypes.func,
  showPhysicalCount: PropTypes.bool,
};
ManualAdjustmentButton.defaultProps = {
  data: null,
  onAdjustClick: null,
  onReorderConfigClick: null,
  onStartCountClick: null,
  onManageCountClick: null,
  onCountHistoryClick: null,
  onCancelCountClick: null,
  onEditDefaultClick: null,
  showPhysicalCount: false,
};

export default ManualAdjustmentButton;
